import GUIDE_IMAGE_SIZES from "@/content/guide-image-sizes.json";
import guideMarkdown from "@/content/user-guide.md?raw";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import { useLocation } from "react-router-dom";
import remarkGfm from "remark-gfm";

/** Anchor id from a heading's text — keeps Korean letters, hyphenates the rest. */
function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "");
}

const TOC = guideMarkdown
  .split("\n")
  .filter((line) => line.startsWith("## "))
  .map((line) => line.slice(3).trim());

const SIZES: Record<string, number[]> = GUIDE_IMAGE_SIZES;

/**
 * 마크다운 렌더러는 모듈 범위에 둔다. 렌더마다 새 함수를 넘기면 React 가 매번 다른 컴포넌트로
 * 보고 제목·사진을 전부 다시 만든다 — 목차 표시(IntersectionObserver)가 떨어진 옛 제목만
 * 보게 되고, 스크롤할 때마다 스크린샷이 새로 그려진다.
 */
const REMARK_PLUGINS = [remarkGfm];
const MARKDOWN_COMPONENTS: Components = {
  h2: ({ children }) => (
    <h2 id={slugify(String(children))} className="scroll-mt-24">
      {children}
    </h2>
  ),
  // 크기를 미리 알려 둬야 늦게 오는 스크린샷이 글을 밀어내지 않는다(guide-image-sizes.json).
  img: ({ src, alt }) => {
    const size = typeof src === "string" ? SIZES[src] : undefined;
    return (
      <img
        src={src}
        alt={alt ?? ""}
        width={size?.[0]}
        height={size?.[1]}
        loading="lazy"
        decoding="async"
      />
    );
  },
};

/**
 * 목차 = 랜딩의 "체류의 선". 세로 헤어라인 위에 장(章)이 정거장처럼 놓이고, 지금 읽는 장에
 * 주황 점이 켜진다. 데스크톱은 옆에 붙고, 모바일은 본문 위에서 접었다 펴는 목록이 된다.
 */
function GuideToc({ active, onPick }: { active: string | null; onPick?: () => void }) {
  return (
    <ol className="guide-toc">
      {TOC.map((title) => {
        const id = slugify(title);
        return (
          <li key={title}>
            <a
              href={`#${id}`}
              onClick={onPick}
              aria-current={active === id ? "location" : undefined}
              className={cn("guide-toc__link", active === id && "is-active")}
            >
              {title}
            </a>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Public usage guide: the operator walkthrough (with the guest experience
 * woven in), rendered from markdown with real product screenshots.
 */
export function UserGuidePage() {
  const { hash } = useLocation();
  const [active, setActive] = useState<string | null>(null);
  const [tocOpen, setTocOpen] = useState(false);

  // 지금 읽는 장: 화면 위쪽 38% 선을 이미 지난 장 제목 가운데 마지막 것. 관찰 영역을 그 선 위로
  // 끝없이 늘려 두면, 빠르게 건너뛰며 스크롤해도 지나간 제목이 모두 "들어온" 상태가 돼서
  // 표시가 중간 장에 멈추지 않는다. 스크롤 이벤트 대신 IntersectionObserver 라 싸다.
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const headings = [...document.querySelectorAll<HTMLElement>(".doc-prose h2[id]")];
    const passed = new Set<string>();
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) passed.add(e.target.id);
          else passed.delete(e.target.id);
        }
        const last = [...headings].reverse().find((h) => passed.has(h.id));
        setActive(last?.id ?? null);
      },
      { rootMargin: "100000px 0px -62% 0px" }
    );
    for (const h of headings) io.observe(h);
    return () => io.disconnect();
  }, []);

  // Deep links like /guide#섹션 land before the browser can resolve the anchor
  // (headings render client-side), so scroll manually — once immediately, once
  // after lazy-loaded screenshots have had a moment to shift the layout.
  useEffect(() => {
    if (!hash) return;
    const id = decodeURIComponent(hash.slice(1));
    const scrollToAnchor = () => document.getElementById(id)?.scrollIntoView();
    scrollToAnchor();
    const settle = window.setTimeout(scrollToAnchor, 400);
    return () => window.clearTimeout(settle);
  }, [hash]);

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-12 sm:px-6 md:py-16">
      <header className="doc-head">
        <h1 className="doc-title">처음 시작하는 운영자를 위한 가이드</h1>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          로그인부터 게스트 응대 시작까지, 실제 화면과 함께 순서대로 따라가는 설정 안내입니다.
        </p>
      </header>

      <div className="mt-10 grid gap-10 lg:grid-cols-[220px_minmax(0,1fr)]">
        <aside className="hidden lg:block">
          <nav aria-label="가이드 목차" className="sticky top-24">
            <GuideToc active={active} />
          </nav>
        </aside>

        {/* 모바일: 2만 px 가 넘는 문서라 위에서 바로 장을 고를 수 있게 접는 목차를 둔다. */}
        <details
          className="guide-toc-mobile lg:hidden"
          open={tocOpen}
          onToggle={(e) => setTocOpen((e.currentTarget as HTMLDetailsElement).open)}
        >
          <summary>목차 · {TOC.length}개 장</summary>
          <nav aria-label="가이드 목차">
            <GuideToc active={active} onPick={() => setTocOpen(false)} />
          </nav>
        </details>

        <article className="doc-prose min-w-0 max-w-3xl">
          <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={MARKDOWN_COMPONENTS}>
            {guideMarkdown}
          </ReactMarkdown>
        </article>
      </div>
    </div>
  );
}
